import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { MAX_VOCA_COUNT } from '../../data/common';
import FoldingGrayIc from '../../assets/images/ic_folding_arrow.svg';
import FoldingBlueIc from '../../assets/images/ic_folding_arrow_blue.svg';
import CheckIc from '../../assets/images/ic_toggle_check_blud.svg';
import DraftRecordSetIc from '../../assets/images/ic_record_set.svg';
import WordStatistics from './WordStatistics';
import QuestionTypeRow from './QuestionTypeRow';
import RecordSetModal from './RecordSetModal';

const TypeList = [
  { idx: 1, type: 'A', title: '한글 뜻 쓰기', maxCount: 0, count: 0 },
  { idx: 2, type: 'B', title: '영단어 쓰기', maxCount: 0, count: 0 },
  { idx: 3, type: 'C', title: '예문 영단어 쓰기', maxCount: 0, count: 0 },
  {
    idx: 4,
    type: 'D',
    title: '유의어/반의어 쓰기',
    maxCount: 0,
    count: 0,
  },
  {
    idx: 5,
    type: 'E',
    title: '파생어 영단어 쓰기',
    maxCount: 0,
    count: 0,
  },
  { idx: 6, type: 'F', title: '음원 듣고 쓰기', maxCount: 0, count: 0 },
  {
    idx: 7,
    type: 'G',
    title: '영영 풀이 보고 영단어 쓰기',
    maxCount: 0,
    count: 0,
  },
  { idx: 8, type: 'H', title: '한글 뜻 고르기', maxCount: 0, count: 0 },
  { idx: 9, type: 'I', title: '영단어 고르기', maxCount: 0, count: 0 },
  { idx: 10, type: 'J', title: '예문 뜻 고르기', maxCount: 0, count: 0 },
  {
    idx: 11,
    type: 'K',
    title: '예문 영단어 고르기',
    maxCount: 0,
    count: 0,
  },
  {
    idx: 12,
    type: 'L',
    title: '유의어/반의어 고르기',
    maxCount: 0,
    count: 0,
  },
  {
    idx: 13,
    type: 'M',
    title: '영영 풀이 보고 영단어 고르기',
    maxCount: 0,
    count: 0,
  },
];

const DraftQuestionTypeRow = ({ type }) => {
  const isDraft = true;
  const [recordSetModalOpen, setRecordSetModalOpen] = useState(false);

  return (
    <div className="border-b border-[#E7E7E7] flex items-center justify-between px-[8px] py-[10px] whitespace-nowrap overflow-hidden">
      <div className="flex gap-1.5 items-center self-stretch my-auto">
        <div className="text-[#A9B6CA] border border-[#A9B6CA] flex justify-center items-center w-[24px] h-[24px] rounded-xl bg-[white] text-[14px] font-[700]">
          {type.type}
        </div>
        <div className="flex gap-1 items-center self-stretch my-auto">
          <div className="w-[20px] h-[20px] text-[12px] font-[400] text-[#A9B6CA] flex justify-center items-center whitespace-nowrap rounded-2xl border border-[#A9B6CA] border-solid min-h-[20px]">
            주
          </div>
          <div className="self-stretch my-auto text-sm leading-none text-[#A9B6CA]">
            {type.title}
          </div>
          {type.type === 'F' ? (
            <img
              src={DraftRecordSetIc}
              alt="DraftRecordSetIc"
              style={{ cursor: 'pointer' }}
              onClick={isDraft ? null : () => setRecordSetModalOpen(true)}
            />
          ) : (
            <img
              src={CheckIc}
              alt="DraftCheckIc"
              style={{ display: type.type === 'L' ? 'none' : 'inline' }}
            />
          )}
          {type.type === 'F' && (
            <RecordSetModal
              open={recordSetModalOpen}
              setOpen={setRecordSetModalOpen}
            />
          )}
        </div>
      </div>
      <div className="flex gap-1 py-[1px] px-[3px] border border-[#A9B6CA] items-center self-stretch my-auto text-[12px] text-[#A9B6CA] font-[500]">
        서비스 준비 중
      </div>
    </div>
  );
};

DraftQuestionTypeRow.propTypes = {
  type: PropTypes.object.isRequired,
};

function QuestionTypeChoice({
  vocabularyList,
  generatedPaper,
  setVocabularyList,
  config,
  setConfig,
  isFolding,
  setIsFolding,
  setRecordSetModalOpen,
  setHideTestPaper,
  checkedStatistics,
  setCheckedStatistics,
}) {
  const [typeList, setTypeList] = useState([]);

  // 출제문항수
  const questionCount = useMemo(() => {
    let count = 0;
    if (vocabularyList?.length >= 1) {
      count = vocabularyList?.filter(
        voca => voca.shouldShow && voca.checked,
      )?.length;
    }
    return count;
  }, [vocabularyList]);

  // 출제가능 문항수
  const availableQuestionCount = useMemo(() => {
    let count = 0;
    if (vocabularyList?.length >= 1) {
      count = vocabularyList?.filter(voca => voca.shouldShow)?.length;
    }
    return count;
  }, [vocabularyList]);

  // 표/파/참/관 체크 이벤트
  const handleCheckStatistics = useCallback(() => {
    const newVocabularyList = vocabularyList.map(voca => {
      if (checkedStatistics.includes(voca.vc_type)) {
        voca.shouldShow = true;
      } else {
        voca.shouldShow = false;
        voca.chosenType = null;
        voca.checked = false;
      }
      return voca;
    });
    setVocabularyList(newVocabularyList);
  }, [vocabularyList, checkedStatistics, setVocabularyList]);
  useEffect(() => {
    handleCheckStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedStatistics]);

  // 타입 데이터 초기화 & vocaList에 타입 변경을 typeList에 반영
  useEffect(() => {
    if (vocabularyList?.length < 1) return;
    const newTypeList = TypeList.map(type => {
      let count = 0;
      let maxCount = 0;
      vocabularyList?.forEach(voca => {
        // 표/파/참/관 shouldShow가 false인 경우 count에 포함하지 않음
        if (!voca.shouldShow) return;
        // 어휘의 문제유형 미결정상태 일 때 : 어휘의 타입리스트 중 type이 있으면 max카운트에 포함
        if (voca.chosenType === null) {
          if (voca.problemTypes?.find(t => t.type === type.type)) {
            maxCount += 1;
          }
        }
        // 어휘의 문제유형 결정상태 일 때 : 결정한 문제유형 타입과 type이 같으면 max카운트에 포함
        else if (voca.chosenType === type.type) {
          maxCount += 1;
        }
        // 어휘 문제유형 결정했고, checked도 한 경우 count에 포함
        if (voca.chosenType === type.type && voca.checked) {
          count += 1;
        }
      });
      type.maxCount = maxCount;
      type.count = count;
      return type;
    });
    setTypeList(newTypeList);
  }, [vocabularyList]);

  // 첫번째 뜻만 체크
  const onCheckFirstMeaningYN = (checked, typeStr) => {
    setConfig(prev => {
      return {
        ...prev,
        onlyFirstMeaning: checked
          ? [...prev.onlyFirstMeaning, typeStr]
          : prev.onlyFirstMeaning.filter(item => item !== typeStr),
      };
    });
    setHideTestPaper(true);
  };

  // 균등 분배하기
  const handleEvenDistribution = useCallback(() => {
    // shouldShow는 true, maxCount >= 1, maxCount작은 것부터, count === maxCount면 스킵, 나머지 순서대로.
    let dynamicTypeList = JSON.parse(JSON.stringify(typeList))
      .filter(type => type.maxCount > 0)
      .sort((a, b) => {
        return a.maxCount - b.maxCount;
      });
    let dynamicVocabularyList = JSON.parse(JSON.stringify(vocabularyList))
      .filter(voca => voca.shouldShow)
      .map(voca => ({ ...voca, checked: false, chosenType: null }));

    let count = 0;
    while (
      !dynamicVocabularyList.every(voca => voca.checked) &&
      count <= MAX_VOCA_COUNT
    ) {
      // eslint-disable-next-line no-loop-func
      dynamicTypeList.forEach(type => {
        if (type.maxCount === type.count) return;
        const targetVocaIndex = dynamicVocabularyList.findIndex(voca => {
          return (
            !voca.checked && voca.problemTypes.some(t => t.type === type.type)
          );
        });
        if (targetVocaIndex === -1) return;
        dynamicVocabularyList[targetVocaIndex].chosenType = type.type;
        dynamicVocabularyList[targetVocaIndex].checked = true;
        count += 1;
        // maxCount 전체 현황 업데이트
        dynamicTypeList = dynamicTypeList.map(type => {
          let count = 0;
          let maxCount = 0;
          dynamicVocabularyList?.forEach(voca => {
            if (voca.chosenType === null) {
              if (voca.problemTypes?.some(t => t.type === type.type)) {
                maxCount += 1;
              } else {
                return;
              }
            } else if (voca.chosenType === type.type) {
              maxCount += 1;
              count += 1;
            }
          });
          type.maxCount = maxCount;
          type.count = count;
          return type;
        });
      });
    }
    // 500개를 초과하면 그 개수만큼, maxCount가 큰 타입부터 순서대로 checked를 false로 변경
    if (count > MAX_VOCA_COUNT) {
      let overCount = count - MAX_VOCA_COUNT;
      let typeListLength = dynamicTypeList?.length;
      Array(overCount)
        .fill(0)
        .forEach((_, index) => {
          // maxCount가 큰 타입부터 순서대로 checked를 false로 변경
          const targetVocaIndex = dynamicVocabularyList.findIndex(voca => {
            return (
              voca.checked &&
              voca.chosenType ===
                dynamicTypeList[typeListLength - 1 - index].type
            );
          });
          if (targetVocaIndex < 0) return;
          dynamicVocabularyList[targetVocaIndex].checked = false;
          dynamicVocabularyList[targetVocaIndex].chosenType = null;
        });
    }
    setVocabularyList(dynamicVocabularyList);
  }, [typeList, vocabularyList, setVocabularyList]);

  // generatePaper 초기화시 필터 초기화
  useEffect(() => {
    if (generatedPaper?.length < 1) {
      setCheckedStatistics([1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedPaper]);

  return (
    <div
      className="bg-[#FFF] h-full min-w-[420px] border-r-[3px] border-[#969ba4] transition-all overflow-hidden"
      style={{
        minWidth: isFolding ? '41px' : '420px',
        width: isFolding ? '41px' : '420px',
      }}
    >
      <div
        className="max-h-[54px] h-[54px] px-[8px] py-[10px] flex items-center justify-between overflow-hidden whiiespace-nowrap border-b border-[#E7E7E7]"
        style={isFolding ? { border: 'none' } : {}}
      >
        <div
          className="flex justify-start items-center gap-[5px]"
          style={{ display: isFolding ? 'none' : 'flex' }}
        >
          <div
            className="flex justify-between items-center gap-[]"
            style={{ display: isFolding ? 'none' : 'flex' }}
          >
            <div className="h-[34px] font-bold text-[14px] flex items-center overflow-hidden">
              출제 문항 수
            </div>
            <div className="w-[50px] h-[32px] text-[24px] font-bold flex justify-center items-center">
              {questionCount}
            </div>
          </div>
          <div className="flex items-center mr-[10px]">/</div>
          <div className="flex justify-between items-center gap-1">
            <div className="h-[32px] text-[14px] flex items-center">
              출제 가능 문항 수
            </div>
            <div className="h-[32px] text-[14px] flex items-center">
              {availableQuestionCount}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            setIsFolding(prev => {
              return { ...prev, type: !prev.type };
            })
          }
        >
          <img
            src={isFolding ? FoldingBlueIc : FoldingGrayIc}
            alt="folding"
            style={{
              minWidth: '22px',
              minHeight: '22px',
            }}
          />
        </button>
      </div>
      {!isFolding && (
        <WordStatistics
          vocabularyList={vocabularyList}
          checkedList={checkedStatistics}
          setCheckedList={setCheckedStatistics}
        />
      )}
      <div
        className="flex justify-center text-[14px] font-[700] mt-[-5px]"
        style={{ display: isFolding ? 'flex' : 'none' }}
      >
        문항
      </div>
      <div
        className="flex gap-1 max-h-[24px] text-[10px] justify-center items-center px-2.5 py-[6px] leading-none border-r bg-slate-300 border-slate-300 text-slate-800 overflow-hidden whitespace-nowrap"
        style={{ display: isFolding ? 'none' : 'flex' }}
      >
        <span style={{ marginLeft: '0px' }}>
          유형 제목 우측 스위치를 클릭해
        </span>
        <img
          src={CheckIc}
          alt="toggle"
          style={{ width: '26px', height: '16px' }}
        />
        <span style={{ marginLeft: '0px' }}>
          와 같이 활성화하면 단어의 첫 번째 뜻만 출제됩니다.
        </span>
      </div>
      <div
        className="grid grid-rows-1 whitespace-nowrap overflow-hidden"
        style={{ display: isFolding ? 'none' : 'grid' }}
      >
        {typeList.map(type => {
          return (
            <QuestionTypeRow
              key={type.idx}
              type={type}
              vocabularyList={vocabularyList}
              onCheckFirstMeaningYN={onCheckFirstMeaningYN}
              setVocabularyList={setVocabularyList}
              questionCount={questionCount}
              setRecordSetModalOpen={setRecordSetModalOpen}
              config={config}
              setConfig={setConfig}
            />
          );
        })}
      </div>
      <div
        className="flex justify-center gap-1 items-center py-[10px] px-[10px] overflow-hidden whitespace-nowrap"
        style={{ display: isFolding ? 'none' : 'flex' }}
      >
        <Button
          variant="gray"
          className="w-[212px] h-[38px]"
          sx={{ fontWeight: 700, fontFamily: 'Pretendard' }}
          onClick={handleEvenDistribution}
          disabled={
            vocabularyList?.length < 1 ||
            questionCount === MAX_VOCA_COUNT ||
            questionCount === availableQuestionCount
          }
        >
          균등 분배하기
        </Button>
      </div>
    </div>
  );
}

QuestionTypeChoice.propTypes = {
  vocabularyList: PropTypes.array.isRequired,
  generatedPaper: PropTypes.array.isRequired,
  setVocabularyList: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  setConfig: PropTypes.func.isRequired,
  isFolding: PropTypes.bool.isRequired,
  setIsFolding: PropTypes.func.isRequired,
  setRecordSetModalOpen: PropTypes.func.isRequired,
  setHideTestPaper: PropTypes.func.isRequired,
  checkedStatistics: PropTypes.array.isRequired,
  setCheckedStatistics: PropTypes.func.isRequired,
};

export default React.memo(QuestionTypeChoice);
